import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigate } from 'react-router';
import { Card, Typography, CircularProgress, Tooltip } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { styled, keyframes } from '@mui/system';
import { setTotalVisits, openModal } from '../../../redux/slices/visits';
import useAuth from '../../../hooks/useAuth';

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 0, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
  }
`;

const ClickableCard = styled(Card)(({ theme, lowVisits }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  border: lowVisits ? '2px solid #ff0000' : '1px solid #3b7299',
  backgroundColor: lowVisits ? '#fff0f0' : theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'all 0.3s',
  animation: lowVisits ? `${pulse} 2s infinite` : 'none',
  '&:hover': {
    backgroundColor: lowVisits ? '#ffe0e0' : theme.palette.action.hover,
    transform: 'scale(1.05)',
  },
}));

const VisitsIndicator = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { totalVisits } = useSelector((state) => state.visits);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(setTotalVisits(user.userVisitLeft?.remaining_visits ? user.userVisitLeft.remaining_visits : 0));
  }, [user, dispatch]);

  const handleClick = () => {
    setIsLoading(true);
    navigate('/checkout');

    // dispatch(openModal());
    // Simulate waiting for modal to open
    // await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
  };

  const getLowVisitsMessage = () => {
    if (totalVisits === 0) {
      return "You're out of visits! Click to add more and continue using our services.";
    }
    if (totalVisits < 5) {
      return 'Low visits!';
    }
    return 'Click to manage your visits';
  };

  const isLowVisits = totalVisits < 5;

  return (
    <Tooltip title={getLowVisitsMessage()} arrow>
      <ClickableCard
        //   onClick={handleClick}
        lowVisits={isLowVisits}
      >
        <Typography variant="body2" sx={{ mr: 1, color: isLowVisits ? 'error.main' : 'inherit' }}>
          Visits:
        </Typography>
        {isLoading ? (
          <CircularProgress size={20} thickness={4} />
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontWeight: 'bold',
              color: isLowVisits ? 'error.main' : 'inherit',
              animation: isLowVisits ? 'blink 1s linear infinite' : 'none',
              '@keyframes blink': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }}
          >
            {totalVisits}
          </Typography>
        )}
      </ClickableCard>
    </Tooltip>
  );
};

export default VisitsIndicator;
